import PropTypes from 'prop-types';
import React from 'react';
import cx from 'classnames';
import { t } from '../../../modules/infra/i18n';
import PermissionName from './PermissionName';
import PermissionCheckbox from './PermissionCheckboxContainer';
import { flagNames, withInjectFeatureFlagsToProps } from '../../../modules/infra/feature-flags';
import Tooltip from '../tooltip';

const Permissions = ({
  shopUser,
  showEngagePermissionForBarbers,
  disableEngagePermission,
  showEngagePermissionTooltip,
  ...props
}) => (
  <div>
    <div className="c-heading c-heading--lightgrey c-wrapper u-pv0 u-mb">{t('admin:users.add.permissions.heading')}</div>
    <div className="u-scrollable-x">
      <table className={cx('c-table-new c-table-permissions c-no-hover', { 'u-ml+': !shopUser })}>
        <thead>
          <tr>
            <th colSpan={2}>{t('admin:users.add.permissions.type_header')}</th>
            <th>{t('admin:users.add.permissions.view_header')}</th>
            <th>{t('admin:users.add.permissions.add_edit_header')}</th>
            <th>{t('admin:users.add.permissions.book_header')}</th>
            <th>{t('admin:users.add.permissions.modify_header')}</th>
            <th>{t('admin:users.add.permissions.history_header')}</th>
            <th>{t('admin:users.add.permissions.cancel_header')}</th>
            <th>{t('admin:users.add.permissions.block_time_header')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <PermissionName>{t('admin:users.add.permissions.appointments_label')}</PermissionName>
            <PermissionCheckbox id="appointments_view" name="permissions.appointments.view" colSpan={2} />
            <PermissionCheckbox id="appointments_book" name="permissions.appointments.book" />
            <PermissionCheckbox id="appointments_modify" name="permissions.appointments.modify" />
            <PermissionCheckbox id="appointments_history" name="permissions.appointments.history" />
            <PermissionCheckbox id="appointments_cancel" name="permissions.appointments.cancel" />
            <PermissionCheckbox
              id="appointments_block-time"
              name={shopUser ? 'permissions.blockTime.modify' : 'permissions.appointments.blockTime'}
            />
          </tr>
          <tr>
            <PermissionName>{t('admin:users.add.permissions.appt_form_show_contact_info_label')}</PermissionName>
            <PermissionCheckbox
              id="appointments_view_contact_info"
              name="permissions.appointments.show_contact_info"
              colSpan={7}
            />
          </tr>
          <tr>
            <PermissionName>{t('admin:users.add.permissions.clients_label')}</PermissionName>
            <PermissionCheckbox id="clients_view" name="permissions.clients.view" />
            <PermissionCheckbox id="clients_modify" name="permissions.clients.modify" colSpan={6} />
          </tr>
          <tr display-if={shopUser}>
            <PermissionName>{t('admin:users.add.permissions.barbers_label')}</PermissionName>
            <PermissionCheckbox id="barbers_view" name="permissions.barbers.view" />
            <PermissionCheckbox id="barbers_modify" name="permissions.barbers.modify" colSpan={6} />
          </tr>
          <tr>
            <PermissionName>{t('admin:users.add.permissions.services_label')}</PermissionName>
            <PermissionCheckbox id="services_view" name="permissions.services.view" />
            <PermissionCheckbox id="services_modify" name="permissions.services.modify" colSpan={6} />
          </tr>
          <tr display-if={!shopUser}>
            <PermissionName>{t('admin:users.add.permissions.schedules_label')}</PermissionName>
            <PermissionCheckbox id="workhours_view" name="permissions.workHours.view" />
            <PermissionCheckbox id="workhours_modify" name="permissions.workHours.modify" colSpan={6} />
          </tr>
          <tr display-if={!shopUser}>
            <PermissionName>{t('admin:users.add.permissions.bank_accounts_label')}</PermissionName>
            <PermissionCheckbox id="bank-account_view" name="permissions.bankAccount.view" />
            <PermissionCheckbox id="bank-account_modify" name="permissions.bankAccount.modify" colSpan={6} />
          </tr>
          <tr>
            <PermissionName>{t('admin:users.add.permissions.profile_label')}</PermissionName>
            <PermissionCheckbox id="profile_view" name="permissions.profile.view" />
            <PermissionCheckbox id="profile_modify" name="permissions.profile.modify" colSpan={6} />
          </tr>
          <tr display-if={props[flagNames.AUTOPAYOUT_SELF_SERVE]}>
            <PermissionName>{t('admin:users.add.permissions.settings_auto_payout_label')}</PermissionName>
            <PermissionCheckbox
              id="auto_payout_view"
              name="permissions.autoPayout.view"
              requiredFor={['permissions.autoPayout.modify']}
              tooltip={t('admin:users.add.permissions.auto_payout_view_tooltip')}
              colSpan={shopUser ? 1 : 7}
            />
            <PermissionCheckbox
              display-if={shopUser}
              id="auto_payout_modify"
              name="permissions.autoPayout.modify"
              colSpan={6}
              requires={['permissions.autoPayout.view']}
              tooltip={t('admin:users.add.permissions.auto_payout_modify_tooltip')}
            />
          </tr>
          <tr display-if={shopUser}>
            <PermissionName>{t('admin:users.add.permissions.settings_shop_details_label')}</PermissionName>
            <PermissionCheckbox id="settings_view" name="permissions.settings.view" />
            <PermissionCheckbox id="settings_modify" name="permissions.settings.modify" colSpan={6} />
          </tr>
          <tr display-if={shopUser}>
            <PermissionName>{t('admin:users.add.permissions.settings_barber_settings_label')}</PermissionName>
            <PermissionCheckbox id="barberSettings_view" name="permissions.barberSettings.view" />
            <PermissionCheckbox id="barberSettings_modify" name="permissions.barberSettings.modify" colSpan={6} />
          </tr>
          <tr display-if={shopUser}>
            <PermissionName>{t('admin:users.add.permissions.settings_bank_account_label')}</PermissionName>
            <PermissionCheckbox id="bank-account_view" name="permissions.bankAccount.view" />
            <PermissionCheckbox id="bank-account_modify" name="permissions.bankAccount.modify" colSpan={6} />
          </tr>
          <tr display-if={shopUser}>
            <PermissionName>{t('admin:users.add.permissions.settings_taxes_label')}</PermissionName>
            <PermissionCheckbox id="taxes_view" name="permissions.taxes.view" />
            <PermissionCheckbox id="taxes_modify" name="permissions.taxes.modify" colSpan={6} />
          </tr>
          <tr display-if={shopUser}>
            <PermissionName>{t('admin:users.add.permissions.settings_discounts_label')}</PermissionName>
            <PermissionCheckbox id="discounts_view" name="permissions.discounts.view" />
            <PermissionCheckbox id="discounts_modify" name="permissions.discounts.modify" colSpan={6} />
          </tr>
          <tr display-if={shopUser}>
            <PermissionName>{t('admin:users.add.permissions.settings_promos_label')}</PermissionName>
            <PermissionCheckbox id="promos_view" name="permissions.promos.view" />
            <PermissionCheckbox id="promos_modify" name="permissions.promos.modify" colSpan={6} />
          </tr>
          <tr display-if={shopUser}>
            <PermissionName>{t('admin:users.add.permissions.settings_waiting_list_label')}</PermissionName>
            <PermissionCheckbox id="waitingList_view" name="permissions.waitingList.view" />
            <PermissionCheckbox id="waitingList_modify" name="permissions.waitingList.modify" colSpan={6} />
          </tr>
          <tr>
            <PermissionName>{t('admin:users.add.permissions.reports_label')}</PermissionName>
            <PermissionCheckbox id="reports_view" name="permissions.reports.view" colSpan={7} />
          </tr>
          <tr display-if={shopUser}>
            <PermissionName>{t('admin:users.add.permissions.time_reports_label')}</PermissionName>
            <PermissionCheckbox id="time_reports_view" name="permissions.timeTrack.view" colSpan={7} />
          </tr>
          <tr display-if={shopUser}>
            <PermissionName>{t('admin:users.add.permissions.products_label')}</PermissionName>
            <PermissionCheckbox
              id="products_view"
              name="permissions.products.view"
              requiredFor={['permissions.products.modify', 'permissions.inventory.modify']}
            />
            <PermissionCheckbox
              id="products_modify"
              name="permissions.products.modify"
              colSpan={6}
              requires={['permissions.products.view']}
            />
          </tr>
          <tr display-if={shopUser}>
            <PermissionName nested>{t('admin:users.add.permissions.inventory_label')}</PermissionName>
            <PermissionCheckbox />
            <PermissionCheckbox
              id="inventory_modify"
              name="permissions.inventory.modify"
              colSpan={6}
              requires={['permissions.products.view']}
              tooltip={t('admin:users.add.permissions.inventory_tooltip')}
            />
          </tr>
          <tr display-if={shopUser}>
            <PermissionName>{t('admin:users.add.permissions.dashboard_label')}</PermissionName>
            <PermissionCheckbox id="dashboard_view" name="permissions.dashboard.view" colSpan={7} />
          </tr>
          <tr display-if={shopUser}>
            <PermissionName>{t('admin:users.add.permissions.analytics_label')}</PermissionName>
            <PermissionCheckbox id="analytics_view" name="permissions.analytics.view" colSpan={7} />
          </tr>
          <tr display-if={shopUser || showEngagePermissionForBarbers}>
            <PermissionName>
              {t('admin:users.add.permissions.engage_label')}
              <Tooltip display-if={showEngagePermissionTooltip}>
                {t('admin:users.add.permissions.engage_tooltip')}
              </Tooltip>
            </PermissionName>
            <PermissionCheckbox
              id="engage_view"
              name="permissions.engage.view"
              colSpan={7}
              disabled={disableEngagePermission}
            />
          </tr>
          <tr>
            <PermissionName>{t('admin:users.add.permissions.register_label')}</PermissionName>
            <PermissionCheckbox id="register_view" name="permissions.register.view" colSpan={7} />
          </tr>
          <tr display-if={shopUser}>
            <PermissionName>{t('admin:users.add.permissions.register_cash_register_label')}</PermissionName>
            <PermissionCheckbox id="cash_register_view" name="permissions.cashRegister.view" colSpan={7} />
          </tr>
          <tr>
            <PermissionName>{t('admin:users.add.permissions.register_open_cash_drawer_label')}</PermissionName>
            <PermissionCheckbox id="cash_register_modify" name="permissions.cashRegister.modify" colSpan={7} />
          </tr>
          <tr display-if={shopUser}>
            <PermissionName>{t('admin:users.add.permissions.register_daily_summary_label')}</PermissionName>
            <PermissionCheckbox id="daily_summary_view" name="permissions.dailySummary.view" colSpan={7} />
          </tr>
          <tr display-if={shopUser}>
            <PermissionName>{t('admin:users.add.permissions.register_daily_summary_history_label')}</PermissionName>
            <PermissionCheckbox
              id="daily_summary_history_view"
              name="permissions.dailySummaryHistory.view"
              colSpan={7}
            />
          </tr>
          <tr>
            <PermissionName>{t('admin:users.add.permissions.register_custom_charge_label')}</PermissionName>
            <PermissionCheckbox id="custom_charge" name="permissions.customCharge.view" colSpan={7} />
          </tr>
          <tr display-if={shopUser}>
            <PermissionName>{t('admin:users.add.permissions.kiosk_label')}</PermissionName>
            <PermissionCheckbox id="kiosk" name="permissions.kiosk.view" colSpan={7} />
          </tr>
        </tbody>
      </table>
    </div>
  </div>
);

Permissions.propTypes = {
  shopUser: PropTypes.bool,
  showEngagePermissionForBarbers: PropTypes.bool,
  showEngagePermissionTooltip: PropTypes.bool,
  disableEngagePermission: PropTypes.bool,
};

Permissions.defaultProps = {
  shopUser: false,
  showEngagePermissionForBarbers: false,
  showEngagePermissionTooltip: false,
  disableEngagePermission: false,
};

export default withInjectFeatureFlagsToProps([flagNames.AUTOPAYOUT_SELF_SERVE])(Permissions);
